import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Map as MapIcon, Layers, ArrowLeft, RefreshCw, Check, Edit2, Save, FileText, Trash2, AlertTriangle } from 'lucide-react';
import Map from '../components/Map';
import type { MapData, FormData } from '../types';
import { fetchSubmissionBySerialNumber, applySubmissionToMaps, SubmissionData, getAllStoredSerialNumbers, getAllSerialNumbers, recoverSubmissions, setSubmissionDisplayName } from '../services/submissionService';

interface GroupResultsPageProps {
  mapsData: MapData[];
  mapStyles: Record<string, 'color' | 'gray' | 'simple'>;
  recentSubmissions?: string[];
  onRemoveSubmission?: (serialNumber: string) => void;
  onRecoverSubmissions?: (serialNumbers: string[]) => void;
  onBack: () => void;
}

// Estilos para a scrollbar personalizada
const scrollbarStyles = `
  .custom-scrollbar::-webkit-scrollbar {
    width: 12px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
    border: 1px solid #e0e0e0;
  }
  
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #6b7280;
    border-radius: 10px;
    border: 2px solid #f1f1f1;
    min-height: 40px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #4b5563;
  }
  
  /* Botões de scroll (elevador) */
  .custom-scrollbar::-webkit-scrollbar-button {
    display: block;
    height: 8px;
    background-color: #e5e7eb;
    border-radius: 4px;
  }
`;

const GroupResultsPage: React.FC<GroupResultsPageProps> = ({ 
  mapsData, 
  mapStyles, 
  recentSubmissions = [], 
  onRemoveSubmission, 
  onRecoverSubmissions,
  onBack
}) => {
  const [activeMap, setActiveMap] = useState<number>(0);
  const [submissions, setSubmissions] = useState<SubmissionData[]>([]);
  const [activeSubmission, setActiveSubmission] = useState<SubmissionData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'current' | 'submission'>('current');
  const [trashCount, setTrashCount] = useState<number>(0);
  const [showLayersPanel, setShowLayersPanel] = useState(false);
  const [isEditingActiveSubmission, setIsEditingActiveSubmission] = useState(false);
  const [activeSubmissionEditName, setActiveSubmissionEditName] = useState('');
  
  // Estado para o modal de confirmação de exclusão
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [submissionToDelete, setSubmissionToDelete] = useState<string | null>(null);
  
  const mapTitles = ['Vista 1 - Cidade', 'Vista 2 - Bairro', 'Vista 3 - Rua'];
  const mapIds = ['city', 'area', 'street'];
  
  // Refs para os mapas
  const mapRefs = useRef<(HTMLDivElement | null)[]>([null, null, null]);
  
  // Carrega uma submissão por número de série
  const handleLoadSubmission = async (directSerialNumber?: string) => {
    const serialToUse = directSerialNumber;
    
    if (!serialToUse) {
      setError('Número de série inválido');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const submission = await fetchSubmissionBySerialNumber(serialToUse);
      
      if (submission) {
        // Adiciona à lista se ainda não existir
        setSubmissions(prev => {
          // Se a submissão já existe na lista, preservamos o displayName existente
          const existingSubmission = prev.find(s => s.serialNumber === submission.serialNumber);
          if (existingSubmission) {
            // Preservamos o displayName se já existir, senão usamos o novo
            const updatedSubmission = { 
              ...submission,
              displayName: existingSubmission.displayName || submission.displayName 
            };
            return prev.map(s => 
              s.serialNumber === submission.serialNumber ? updatedSubmission : s
            );
          }
          
          // Caso ainda não exista, apenas adiciona
          if (!prev.some(s => s.serialNumber === submission.serialNumber)) {
            return [...prev, submission];
          }
          return prev;
        });
        
        setActiveSubmission(submission);
        setViewMode('submission');
      } else {
        setError('Submissão não encontrada');
      }
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('Erro ao buscar submissão');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Obtém os mapas modificados com base na submissão selecionada
  const getSubmissionMaps = (): MapData[] => {
    if (!activeSubmission) return mapsData;
    return applySubmissionToMaps(mapsData, activeSubmission);
  };
  
  // Obtém as camadas ativas para um determinado mapa
  const getActiveLayers = (mapIndex: number) => {
    const maps = viewMode === 'submission' && activeSubmission 
      ? getSubmissionMaps() 
      : mapsData;
    
    const map = maps[mapIndex];
    return map.layers.filter(layer => layer.isActive);
  };
  
  // Obtém o estilo do mapa para exibição
  const getMapStyle = (mapIndex: number): 'color' | 'gray' | 'simple' => {
    if (viewMode === 'submission' && activeSubmission) {
      if (mapIndex === 0) return activeSubmission.mapStyles.city;
      if (mapIndex === 1) return activeSubmission.mapStyles.area;
      if (mapIndex === 2) return activeSubmission.mapStyles.street;
    }
    
    return mapStyles[mapIds[mapIndex]];
  };
  
  // Redefine para o modo atual
  const resetToCurrentMaps = () => {
    setViewMode('current');
    setActiveSubmission(null);
  };
  
  // Remove a submissão atual da visualização
  const removeCurrentSubmission = () => {
    if (!activeSubmission) return;
    
    // Abrir modal de confirmação
    setSubmissionToDelete(activeSubmission.serialNumber);
    setIsDeleteModalOpen(true);
  };
  
  // Função para confirmar exclusão de submissão
  const confirmDelete = () => {
    if (!submissionToDelete) return;
    
    if (onRemoveSubmission) {
      onRemoveSubmission(submissionToDelete);
    }
    
    // Se a submissão a ser excluída é a ativa atualmente
    if (activeSubmission?.serialNumber === submissionToDelete) {
      setActiveSubmission(null);
      setViewMode('current');
    }
    
    // Remover da lista de submissões carregadas
    setSubmissions(prev => prev.filter(s => s.serialNumber !== submissionToDelete));
    
    // Fechar o modal e limpar referência
    setIsDeleteModalOpen(false);
    setSubmissionToDelete(null);
  };
  
  // Função para cancelar exclusão
  const cancelDelete = () => {
    setIsDeleteModalOpen(false);
    setSubmissionToDelete(null);
  };
  
  // Efeito para carregar submissões recentes quando o componente é montado
  useEffect(() => {
    // Quando o componente é montado, vamos pré-carregar as submissões recentes
    const loadRecentSubmissions = async () => {
      if (recentSubmissions.length > 0) {
        setIsLoading(true);
        try {
          const loadedSubmissions: SubmissionData[] = [];
          
          for (const serial of recentSubmissions) {
            try {
              const submission = await fetchSubmissionBySerialNumber(serial);
              if (submission) {
                loadedSubmissions.push(submission);
              }
            } catch (error) {
              console.error(`Erro ao carregar submissão ${serial}:`, error);
            }
          }
          
          // Atualiza o estado com todas as submissões carregadas
          setSubmissions(prev => {
            // Combine previous and new submissions, preserving displayNames
            const combined = [...prev];
            
            loadedSubmissions.forEach(newSub => {
              const existingIndex = combined.findIndex(s => s.serialNumber === newSub.serialNumber);
              
              if (existingIndex >= 0) {
                // Se já existe, preservamos o displayName se tiver
                combined[existingIndex] = { 
                  ...newSub,
                  displayName: combined[existingIndex].displayName || newSub.displayName 
                };
              } else {
                // Se não existe, adicionamos
                combined.push(newSub);
              }
            });
            
            return combined;
          });
        } catch (error) {
          console.error('Erro ao carregar submissões recentes:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };
    
    loadRecentSubmissions();
  }, [recentSubmissions]);
  
  // Efeito para verificar submissões quando o componente é montado
  useEffect(() => {
    // Quando o componente é montado, verifica se há submissões no localStorage
    const storedSubmissions = getAllStoredSerialNumbers();
    console.log("Página aberta - submissões armazenadas:", storedSubmissions);
    
    // Se houver submissões armazenadas e nenhuma nos recentSubmissions, tenta recuperá-las
    if (storedSubmissions.length > 0 && recentSubmissions.length === 0) {
      if (onRecoverSubmissions) {
        console.log("Recuperando submissões ao abrir página");
        onRecoverSubmissions(storedSubmissions.slice(0, 5)); // Limita a 5
      }
    }
    
    // Verifica quantas submissões estão na lixeira
    const allSubmissions = getAllSerialNumbers();
    const trashSubmissions = allSubmissions.filter(sn => !storedSubmissions.includes(sn));
    setTrashCount(trashSubmissions.length);

    // Adicionar os estilos da scrollbar ao head
    const styleElement = document.createElement('style');
    styleElement.innerHTML = scrollbarStyles;
    document.head.appendChild(styleElement);
    
    // Cleanup - remover os estilos quando o componente for desmontado
    return () => {
      document.head.removeChild(styleElement);
    };
  }, [onRecoverSubmissions, recentSubmissions.length]);
  
  // Efeito para sincronizar as alterações de nomes entre submissões
  useEffect(() => {
    // Quando o componente é renderizado, garante que todas as submissões carregadas
    // tenham seus nomes atualizados no serviço de persistência local
    submissions.forEach(submission => {
      if (submission.displayName) {
        // Garante que o displayName está salvo no serviço
        setSubmissionDisplayName(submission.serialNumber, submission.displayName);
      }
    });
  }, [submissions]);
  
  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Modal de confirmação de exclusão */}
      <AnimatePresence>
        {isDeleteModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4"
            >
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Confirmar exclusão</h3>
                <p className="text-sm text-gray-500 mb-6">
                  Tem certeza que deseja apagar esta submissão? Esta ação não pode ser desfeita.
                </p>
                <div className="flex flex-col-reverse sm:flex-row sm:justify-center gap-3">
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md border border-gray-300 px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                    onClick={cancelDelete}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md border border-transparent px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:w-auto sm:text-sm"
                    onClick={confirmDelete}
                  >
                    Sim, apagar
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <header className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-20 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <button 
              onClick={onBack} 
              className="text-gray-600 hover:text-gray-900 mr-4 flex items-center gap-1"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Voltar</span>
            </button>
            <h1 className="text-2xl font-bold text-gray-900">
              {viewMode === 'submission' && activeSubmission 
                ? `${activeSubmission.displayName || activeSubmission.serialNumber}` 
                : 'Resultados do Grupo - Discussão'}
            </h1>
          </div>
          
          <div className="flex items-center gap-3">
            {viewMode === 'submission' && (
              <>
                <button
                  onClick={() => {
                    // Ativa o modo de edição diretamente
                    setActiveSubmissionEditName(activeSubmission?.displayName || '');
                    setIsEditingActiveSubmission(true);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-md transition-colors flex items-center gap-2 text-sm text-gray-600"
                  title="Editar nome da submissão"
                >
                  <Edit2 className="w-4 h-4" />
                  <span>Editar</span>
                </button>
                <button
                  onClick={removeCurrentSubmission}
                  className="p-2 hover:bg-red-100 rounded-md transition-colors flex items-center gap-2 text-sm text-red-600"
                  title="Remover esta submissão"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Remover</span>
                </button>
                <button
                  onClick={resetToCurrentMaps}
                  className="p-2 hover:bg-blue-100 rounded-md transition-colors flex items-center gap-2 text-sm text-blue-600"
                >
                  <RefreshCw className="w-4 h-4" />
                  Voltar ao mapa atual
                </button>
              </>
            )}
          </div>
        </div>
        
        {/* Modal de edição da submissão ativa */}
        {isEditingActiveSubmission && activeSubmission && (
          <div className="mt-3 p-3 bg-white rounded-lg border border-blue-200 shadow-sm">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={activeSubmissionEditName}
                onChange={(e) => setActiveSubmissionEditName(e.target.value)}
                onKeyDown={(e) => {
                  // Salvar ao pressionar Enter
                  if (e.key === 'Enter' && activeSubmissionEditName.trim()) {
                    const newName = activeSubmissionEditName.trim();
                    
                    // Atualizar imediatamente o serviço de persistência
                    setSubmissionDisplayName(activeSubmission.serialNumber, newName);
                    
                    // Atualização mais direta do estado local
                    setActiveSubmission(prev => ({
                      ...prev!,
                      displayName: newName
                    }));
                    
                    setSubmissions(prev => 
                      prev.map(s => 
                        s.serialNumber === activeSubmission.serialNumber
                          ? { ...s, displayName: newName } 
                          : s
                      )
                    );
                    
                    // Sair do modo de edição imediatamente
                    setIsEditingActiveSubmission(false);
                  }
                  // Cancelar ao pressionar Escape
                  else if (e.key === 'Escape') {
                    setIsEditingActiveSubmission(false);
                  }
                }}
                placeholder="Digite um nome para a submissão"
                className="text-sm border border-blue-300 rounded-md px-3 py-1 flex-grow bg-white"
                autoFocus
              />
              <button
                onClick={() => {
                  if (activeSubmissionEditName.trim()) {
                    // Processo simplificado e otimizado para salvar o nome mais rapidamente
                    const newName = activeSubmissionEditName.trim();
                    
                    // Atualizar imediatamente o serviço de persistência
                    setSubmissionDisplayName(activeSubmission.serialNumber, newName);
                    
                    // Atualização mais direta do estado local
                    setActiveSubmission(prev => ({
                      ...prev!,
                      displayName: newName
                    }));
                    
                    setSubmissions(prev => 
                      prev.map(s => 
                        s.serialNumber === activeSubmission.serialNumber
                          ? { ...s, displayName: newName } 
                          : s
                      )
                    );
                    
                    // Sair do modo de edição imediatamente
                    setIsEditingActiveSubmission(false);
                  } else {
                    // Se o campo estiver vazio, apenas sair do modo de edição
                    setIsEditingActiveSubmission(false);
                  }
                }}
                className="p-1 bg-green-100 text-green-700 border border-green-200 hover:bg-green-200 rounded-md text-sm flex items-center"
              >
                <Save className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => {
                  setIsEditingActiveSubmission(false);
                }}
                className="p-1 bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200 rounded-md text-sm"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            
            {activeSubmission.comments && (
              <div className="mt-2 text-xs text-gray-700 bg-blue-50 p-2 rounded border border-blue-100">
                <div className="flex items-start">
                  <FileText className="w-3 h-3 text-blue-500 mt-0.5 mr-1 flex-shrink-0" />
                  <p>{activeSubmission.comments}</p>
                </div>
              </div>
            )}
            
            <div className="mt-1 text-xs text-blue-600">
              <span className="font-semibold">ID:</span> {activeSubmission.serialNumber} | 
              <span className="font-semibold ml-1">Data:</span> {activeSubmission.submissionDate}
            </div>
          </div>
        )}
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Submissões recentes do grupo */}
        {recentSubmissions.length > 0 && (
          <div className="mb-6 bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="flex flex-wrap justify-between items-center mb-3">
              <h4 className="text-base font-medium text-blue-700 flex items-center">
                <span className="inline-block w-3 h-3 rounded-full bg-blue-500 mr-2"></span>
                Submissões recentes do seu grupo:
              </h4>
            </div>
            
            <div className="flex flex-wrap items-center gap-3 mb-2">
              {recentSubmissions.map((serial, idx) => {
                const submissionData = submissions.find(s => s.serialNumber === serial);
                const displayName = submissionData?.displayName;
                
                return (
                  <div key={`recent-${idx}`} className="inline-block">
                    <div className="flex items-center">
                      <button
                        onClick={() => handleLoadSubmission(serial)}
                        className={`text-xs px-3 py-1.5 rounded-l-full ${
                          activeSubmission?.serialNumber === serial
                            ? 'bg-blue-100 text-blue-800 border border-blue-300'
                            : 'bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100'
                        }`}
                      >
                        {displayName || serial}
                      </button>
                      {/* Botão para editar o nome */}
                      <button
                        onClick={() => {
                          // Simplificação - carregar a submissão e imediatamente entrar no modo de edição
                          if (!submissions.some(s => s.serialNumber === serial)) {
                            handleLoadSubmission(serial);
                          }
                          
                          // Ativa o modo de edição diretamente
                          const submissionData = submissions.find(s => s.serialNumber === serial);
                          if (submissionData) {
                            setActiveSubmission(submissionData);
                            setActiveSubmissionEditName(submissionData.displayName || '');
                            setIsEditingActiveSubmission(true);
                            setViewMode('submission');
                          }
                        }}
                        className="text-xs px-2 py-1.5 bg-gray-100 text-gray-700 border-y border-l border-gray-200 hover:bg-gray-200"
                        title="Editar nome da submissão"
                      >
                        <Edit2 className="w-3 h-3" />
                      </button>
                      {/* Botão para remover a submissão */}
                      <button
                        onClick={() => {
                          // Configurar dados para o modal de confirmação
                          setSubmissionToDelete(serial);
                          setIsDeleteModalOpen(true);
                        }}
                        className="text-xs px-2 py-1.5 bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 rounded-r-full"
                        title="Remover esta submissão"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            
            <p className="text-xs text-blue-600 italic mt-2">
              <span className="font-semibold">Dica:</span> Clique em uma submissão para carregá-la ou use o botão de edição para renomeá-la
            </p>
          </div>
        )}
        
        {/* Layout principal - mapa fixo com painel à direita */}
        <div className="flex h-full">
          {/* Botões de navegação no lado esquerdo */}
          <div className="flex flex-col gap-3 mr-4 self-start pt-4">
            {mapTitles.map((title, index) => (
              <button
                key={index}
                className={`px-3 py-2.5 rounded-md transition-colors text-sm w-36 text-left ${
                  activeMap === index
                    ? 'bg-blue-500 text-white font-medium shadow-sm'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
                onClick={() => setActiveMap(index)}
              >
                {title}
              </button>
            ))}
          </div>
          
          {/* Área principal do mapa e controles */}
          <div className="flex-1 relative flex">
            {/* Área do mapa fixo à esquerda */}
            <div className="flex-shrink-0">
              {/* Mapa com tamanho ajustado para MacBook Pro 16" */}
              <div className="w-[900px] h-[650px] relative">
                <Map
                  data={{
                    ...(viewMode === 'submission' && activeSubmission ? getSubmissionMaps() : mapsData)[activeMap],
                    baseImageUrl: 
                      viewMode === 'submission' && activeSubmission
                        ? (viewMode === 'submission' && activeSubmission ? getSubmissionMaps() : mapsData)[activeMap].baseImageUrl
                        : mapStyles[mapIds[activeMap]] === 'gray' 
                          ? (viewMode === 'submission' && activeSubmission ? getSubmissionMaps() : mapsData)[activeMap].baseImageUrlGray 
                          : mapStyles[mapIds[activeMap]] === 'simple' 
                            ? (viewMode === 'submission' && activeSubmission ? getSubmissionMaps() : mapsData)[activeMap].baseImageUrlSimple 
                            : (viewMode === 'submission' && activeSubmission ? getSubmissionMaps() : mapsData)[activeMap].baseImageUrl
                  }}
                  onMapRef={(ref) => {
                    if (mapRefs.current) {
                      mapRefs.current[activeMap] = ref;
                    }
                  }}
                />
              </div>
            </div>
            
            {/* Área à direita para o botão e painel de camadas */}
            <div className="ml-4 flex-shrink-0 flex flex-col">
              {/* Botão de camadas */}
              <button 
                className="bg-white text-blue-600 p-3 rounded-md shadow-md z-40 flex items-center justify-center h-12 mb-4 self-start"
                onClick={() => setShowLayersPanel(prev => !prev)}
                title={showLayersPanel ? "Fechar painel de camadas" : "Ver camadas"}
              >
                <Layers className="w-6 h-6 mr-2" />
                <span>{showLayersPanel ? "Fechar camadas" : "Ver camadas"}</span>
              </button>
              
              {/* Painel de camadas fixo à direita */}
              <AnimatePresence>
                {showLayersPanel && (
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    className="bg-white shadow-xl border border-gray-200 rounded-lg overflow-hidden z-50 h-[600px] w-[320px]"
                  >
                    <div className="p-4 h-full flex flex-col">
                      <div className="flex justify-between items-center mb-3">
                        <h3 className="font-medium text-gray-800 flex items-center">
                          <Layers className="w-5 h-5 text-blue-500 mr-2" />
                          Camadas
                        </h3>
                        <button 
                          className="p-1 hover:bg-gray-100 rounded-full"
                          onClick={() => setShowLayersPanel(false)}
                        >
                          <X className="w-5 h-5 text-gray-500" />
                        </button>
                      </div>
                      
                      {/* Estilo do mapa */}
                      <div className="mb-4 bg-blue-50 p-2 rounded-md">
                        <div className="flex items-center">
                          <span className="text-blue-600 font-medium">Estilo: </span>
                          <span className="ml-2">
                            {getMapStyle(activeMap) === 'color' && 'Cor'}
                            {getMapStyle(activeMap) === 'gray' && 'Cinza'}
                            {getMapStyle(activeMap) === 'simple' && 'Simplificado'}
                          </span>
                        </div>
                      </div>
                      
                      {/* Container com scrollbar personalizada para as camadas */}
                      <div className="flex-1 overflow-y-auto pr-4 custom-scrollbar">
                        {(() => {
                          const activeLayers = getActiveLayers(activeMap);
                          
                          if (activeLayers.length === 0) {
                            return <p className="text-gray-500 italic text-sm">Nenhuma camada selecionada</p>;
                          }
                          
                          // Agrupar camadas por seção
                          const groupedLayers: Record<string, typeof activeLayers> = {};
                          
                          activeLayers.forEach(layer => {
                            // Mapear seções antigas para novas (como no email)
                            let section = layer.section || '';
                            
                            if (section === 'Outros') section = 'Elementos de Suporte';
                            if (section === 'Transportes') section = 'Transportes Públicos';
                            if (!section && ['green-zones', 'zone30', 'volume-transito'].includes(layer.id)) {
                              section = 'Zonas a Destacar';
                            }
                            
                            if (!groupedLayers[section]) groupedLayers[section] = [];
                            groupedLayers[section].push(layer);
                          });
                          
                          // Renderizar cada grupo
                          return Object.entries(groupedLayers).map(([section, sectionLayers]) => (
                            <div key={section} className="mb-4">
                              <h4 className="text-sm font-semibold bg-blue-50 px-3 py-1.5 rounded border-l-2 border-blue-500 mb-2">
                                {section || 'Outras Camadas'}
                              </h4>
                              <ul className="flex flex-col gap-y-1 pl-2">
                                {sectionLayers.map((layer, idx) => (
                                  <li key={idx} className="text-sm flex items-center gap-1 truncate">
                                    <Check className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                                    <span className="truncate">{layer.name}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ));
                        })()}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default GroupResultsPage; 